'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdLocalPharmacy, MdSearch } from 'react-icons/md'
import {
    getDispensation,
    createDispensation,
    CreateDispensationData,
} from '@/lib/api/stock/dispensation-api'
import { getMedications, Medication } from '@/lib/api/stock/medication-api'
import { getBatches, MedicationBatch } from '@/lib/api/stock/batch-api'
import { ClientSelector } from '@/components/client/ClientSelector'

export default function DispensationFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [medications, setMedications] = useState<Medication[]>([])
    const [batches, setBatches] = useState<MedicationBatch[]>([])
    const [medicationSearch, setMedicationSearch] = useState('')
    const [selectedMedication, setSelectedMedication] =
        useState<Medication | null>(null)

    const [formData, setFormData] = useState<CreateDispensationData>({
        clientId: 0,
        medicationId: 0,
        prescribedQuantity: 1,
        isControlled: false,
        isSubstitution: false,
    })

    useEffect(() => {
        loadInitialData()
    }, [])

    useEffect(() => {
        if (isEditing) {
            loadDispensation()
        }
    }, [id])

    useEffect(() => {
        if (formData.medicationId) {
            loadAvailableBatches()
        }
    }, [formData.medicationId])

    const loadInitialData = async () => {
        try {
            const medResponse = await getMedications()

            if (medResponse.success) {
                setMedications(medResponse.result)
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        }
    }

    const loadDispensation = async () => {
        setLoading(true)
        try {
            const response = await getDispensation(Number(id))
            if (response.success) {
                const disp = response.result
                setFormData({
                    clientId: disp.clientId,
                    medicationId: disp.medicationId,
                    batchId: disp.batchId,
                    prescribedQuantity: disp.prescribedQuantity,
                    isControlled: disp.isControlled,
                    isSubstitution: disp.isSubstitution,
                    substitutionReason: disp.substitutionReason,
                    originalMedicationId: disp.originalMedicationId,
                    instructions: disp.instructions,
                    notes: disp.notes,
                })
                if (disp.medication) {
                    setSelectedMedication(disp.medication as Medication)
                }
            } else {
                toast.error(response.error || 'Erro ao carregar dispensação')
                navigate('/stock/dispensação')
            }
        } catch (error) {
            toast.error('Erro ao carregar dispensação')
            navigate('/stock/dispensação')
        } finally {
            setLoading(false)
        }
    }

    const loadAvailableBatches = async () => {
        try {
            const response = await getBatches({
                medicationId: formData.medicationId,
                status: 'BATCH_ACTIVE',
            })
            if (response.success) {
                // Sort by expiration date (FEFO)
                const sorted = response.result.sort(
                    (a, b) =>
                        new Date(a.expirationDate).getTime() -
                        new Date(b.expirationDate).getTime()
                )
                setBatches(sorted)
            }
        } catch (error) {
            console.error('Erro ao carregar lotes:', error)
        }
    }

    const handleMedicationSelect = (medication: Medication) => {
        setSelectedMedication(medication)
        setFormData({
            ...formData,
            medicationId: medication.id,
            isControlled: medication.requiresSngpc,
        })
        setMedicationSearch('')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.clientId) {
            toast.error('Selecione um paciente')
            return
        }

        if (!formData.medicationId) {
            toast.error('Selecione um medicamento')
            return
        }

        if (formData.prescribedQuantity <= 0) {
            toast.error('Quantidade deve ser maior que zero')
            return
        }

        setSaving(true)
        try {
            const response = await createDispensation(formData)

            if (response.success) {
                toast.success('Dispensação criada com sucesso')
                navigate('/stock/dispensação')
            } else {
                toast.error(response.error || 'Erro ao criar dispensação')
            }
        } catch (error) {
            toast.error('Erro ao criar dispensação')
        } finally {
            setSaving(false)
        }
    }

    const filteredMedications = medicationSearch
        ? medications.filter(
              m =>
                  m.name
                      .toLowerCase()
                      .includes(medicationSearch.toLowerCase()) ||
                  m.genericName
                      ?.toLowerCase()
                      .includes(medicationSearch.toLowerCase())
          )
        : []

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/stock/dispensação')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center shadow-lg">
                        <MdLocalPharmacy className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Dispensação'
                                : 'Nova Dispensação'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da dispensação'
                                : 'Registre uma nova dispensação de medicamento'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">Paciente</h2>
                    <ClientSelector
                        value={formData.clientId || null}
                        onChange={clientId =>
                            setFormData(prev => ({
                                ...prev,
                                clientId: clientId ?? 0,
                            }))
                        }
                        label=""
                        placeholder="Buscar paciente por nome, CPF, e-mail ou WhatsApp..."
                    />
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">Medicamento</h2>
                    <div className="form-control">
                        {selectedMedication ? (
                            <div className="flex items-center justify-between p-3 bg-base-200/50 rounded-lg">
                                <div>
                                    <p className="font-semibold">
                                        {selectedMedication.name}
                                    </p>
                                    {selectedMedication.genericName && (
                                        <p className="text-sm text-base-content/60">
                                            {selectedMedication.genericName}
                                        </p>
                                    )}
                                    <div className="flex gap-2 mt-1">
                                        <span className="badge badge-sm badge-ghost">
                                            {selectedMedication.unit}
                                        </span>
                                        {selectedMedication.requiresSngpc && (
                                            <span className="badge badge-sm badge-warning">
                                                Controlado
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => {
                                        setSelectedMedication(null)
                                        setFormData({
                                            ...formData,
                                            medicationId: 0,
                                            batchId: undefined,
                                            isControlled: false,
                                        })
                                        setBatches([])
                                    }}
                                >
                                    Alterar
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                                <input
                                    type="text"
                                    className="input input-bordered w-full pl-10"
                                    placeholder="Buscar medicamento..."
                                    value={medicationSearch}
                                    onChange={e =>
                                        setMedicationSearch(e.target.value)
                                    }
                                />
                                {filteredMedications.length > 0 && (
                                    <div className="absolute z-20 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                                        {filteredMedications.map(med => (
                                            <div
                                                key={med.id}
                                                className="p-3 hover:bg-base-200 cursor-pointer"
                                                onClick={() =>
                                                    handleMedicationSelect(med)
                                                }
                                            >
                                                <p className="font-semibold">
                                                    {med.name}
                                                </p>
                                                {med.genericName && (
                                                    <p className="text-sm text-base-content/60">
                                                        {med.genericName}
                                                    </p>
                                                )}
                                                <div className="flex gap-2 mt-1">
                                                    <span className="badge badge-sm badge-ghost">
                                                        {med.unit}
                                                    </span>
                                                    {med.requiresSngpc && (
                                                        <span className="badge badge-sm badge-warning">
                                                            Controlado
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {selectedMedication && batches.length > 0 && (
                        <div className="form-control mt-4">
                            <label className="label">
                                <span className="label-text">
                                    Lote (FEFO aplicado)
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.batchId || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        batchId: e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                    })
                                }
                            >
                                <option value="">
                                    Automatico (primeiro a vencer)
                                </option>
                                {batches.map(batch => (
                                    <option key={batch.id} value={batch.id}>
                                        {batch.batchNumber} - Venc:{' '}
                                        {new Date(
                                            batch.expirationDate
                                        ).toLocaleDateString('pt-BR')}{' '}
                                        - Qtd: {batch.currentQuantity}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Detalhes da Dispensação
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Quantidade Prescrita *
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                min="1"
                                value={formData.prescribedQuantity}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        prescribedQuantity:
                                            parseInt(e.target.value) || 1,
                                    })
                                }
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.isSubstitution}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            isSubstitution: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">
                                    Substituição de medicamento
                                </span>
                            </label>
                        </div>

                        {formData.isSubstitution && (
                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">
                                        Motivo da Substituição
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    placeholder="Ex: Medicamento original em falta"
                                    value={formData.substitutionReason || ''}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            substitutionReason: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        )}

                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text">
                                    Instruções de Uso
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={2}
                                placeholder="Ex: Tomar 1 comprimido de 8 em 8 horas"
                                value={formData.instructions || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        instructions: e.target.value,
                                    })
                                }
                            ></textarea>
                        </div>

                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text">Observações</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                rows={2}
                                placeholder="Observações internas..."
                                value={formData.notes || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        notes: e.target.value,
                                    })
                                }
                            ></textarea>
                        </div>
                    </div>
                </div>

                {formData.isControlled && (
                    <div className="alert alert-warning">
                        <span>
                            Este medicamento é controlado e será registrado no
                            SNGPC após a dispensação.
                        </span>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => navigate('/stock/dispensação')}
                        className="btn btn-ghost"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        Criar Dispensação
                    </button>
                </div>
            </form>
        </div>
    )
}
